import { useCallback, useRef } from "react";
import { IntegrationAccount } from "../../common/types/integrationAccount";
import { useFinancialAccounts } from "../../hooks/useFinancialAccounts";
import { AccountCard } from "../general/AccountCard/AccountCard";
import {
    PopoverContainer,
    PopoverContainerHandle,
} from "../general/PopoverContainer";
import { SelectDropdown } from "../general/SelectDropdown/SelectDropdown";
import { SelectDropdownDivider } from "../general/SelectDropdown/SelectDropdownDivider";
import { SelectDropdownOption } from "../general/SelectDropdown/SelectDropdownOption";
import { FinancialAccount } from "../../common/types/financialAccount";
import {
    ImporterAccountPickerTrigger,
    IntegrationAccountChangePayload,
} from "./ImporterAccountPickerTrigger";

interface Props {
    integrationAccount: IntegrationAccount;
    onChange: (payload: Partial<IntegrationAccount>) => void;
    selectedFinancialAccount?: FinancialAccount;
}

export const ImporterAccountPicker: React.FC<Props> = ({
    integrationAccount,
    onChange,
    selectedFinancialAccount,
}) => {
    const popoverRef = useRef<PopoverContainerHandle>(null);
    const financialAccounts = useFinancialAccounts({
        excludeMockEntities: true,
    });

    const enableNewAccountMode = useCallback(() => {
        if (integrationAccount.originalFinancialAccountId) {
            onChange({
                originalFinancialAccountId: null,
                originalAccountNumber: "",
            });
        }
        popoverRef.current?.close();
    }, [integrationAccount.originalFinancialAccountId, onChange]);

    const selectExistingAccount = useCallback(
        (account: FinancialAccount) => {
            onChange({
                originalFinancialAccountId: account.id,
                originalAccountNumber: null,
                originalEntityId: null,
            });
            popoverRef.current?.close();
        },
        [onChange],
    );

    const onAccountChanged = useCallback(
        (payload: IntegrationAccountChangePayload) => {
            onChange(payload);
        },
        [onChange],
    );

    return (
        <PopoverContainer
            id={`importer-account-picker-${integrationAccount.id}`}
            trigger={
                <ImporterAccountPickerTrigger
                    integrationAccount={integrationAccount}
                    selectedFinancialAccount={selectedFinancialAccount}
                    onAccountChanged={onAccountChanged}
                />
            }
            ref={popoverRef}
        >
            <SelectDropdown>
                <SelectDropdownOption onClick={enableNewAccountMode}>
                    Create new account
                </SelectDropdownOption>
                <SelectDropdownDivider />
                <span className="label ml-3 mb-1">Use existing account</span>
                {financialAccounts.map((account) => (
                    <SelectDropdownOption
                        key={account.id}
                        onClick={() => selectExistingAccount(account)}
                    >
                        <AccountCard
                            account={account}
                            iconSize="sm"
                            showNumber
                        />
                    </SelectDropdownOption>
                ))}
            </SelectDropdown>
        </PopoverContainer>
    );
};
